import React, { useEffect, useState, useCallback } from 'react';
import { IoMdInformationCircle } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';

import { Card, CardContent, Grid, Typography, Radio } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import ArrowImg from '~/assets/Arrow.svg';
import BarCharts from '~/components/Charts/Bar';
import Pie from '~/components/Charts/Pie';
import StatusBar from '~/components/Charts/StatusBar';
import Calendar from '~/components/DatePicker/Calendar';
import { useAuth } from '~/hooks/auth';
import { IState } from '~/store';
import { getProcessesCountByStatusRequest } from '~/store/modules/processes/actions';
import { IPaginateValues } from '~/store/modules/processes/types';
import {
  getTaskDataChartRequest,
  getTasksStatusRequest,
} from '~/store/modules/tasks/actions';

import {
  Container,
  Box,
  Content,
  BarchartCard,
  FormControl,
  FormControlLabel,
  RadioGroup,
  TitleWrapper,
  Header,
  Square,
  Wrapper,
  LeftButton,
  RightButton,
} from './styles';

type TasksType =
  | 'allTasks'
  | 'assignedToMe'
  | 'assignedToOthers'
  | 'unassignedTasks';

const Dashboard: React.FC = () => {
  const LIMIT = 5;

  const [tasksType, setTasksType] = useState<TasksType>('assignedToMe');
  const [pieTasksType, setPieTasksType] = useState<TasksType>('allTasks');

  const [isValidDateRange, setIsValidDateRange] = useState(false);
  const [dateRange, setDateRange] = useState<Date[]>([]);

  const { user } = useAuth();

  const dispatch = useDispatch();
  const [page, setPage] = useState(1);

  const tasksDataChart = useSelector(
    (state: IState) => state.tasks.tasksDataChart,
  );

  const tasksDataChartLoading = useSelector(
    (state: IState) => state.tasks.loadingTaskDataChart,
  );

  const tasksStatusCount = useSelector(
    (state: IState) => state.tasks.tasksStatusCount,
  );

  const processesCountByStatus = useSelector(
    (state: IState) => state.processes.processesCountByStatus,
  );

  const processesTotalCount = useSelector(
    (state: IState) => state.processes.total,
  );

  const handleChangeRadio = useCallback((event) => {
    const { value } = event.target;

    setTasksType(value);
  }, []);

  const handlePieTaskType = useCallback((event) => {
    const { value } = event.target;

    setPieTasksType(value);
  }, []);

  useEffect(() => {
    const params = {};

    if (tasksType === 'assignedToMe') {
      Object.assign(params, {
        assignee: user.personId,
      });
    } else if (tasksType === 'assignedToOthers') {
      Object.assign(params, {
        assigneeNotIn: [user.personId],
      });
    } else {
      const groups = user.groups.map((group) => group.id);

      Object.assign(params, {
        candidateGroups: groups,
        unassigned: true,
      });
    }

    dispatch(getTasksStatusRequest(params));
  }, [dispatch, tasksType, user.groups, user.personId]);

  useEffect(() => {
    if (dateRange.length === 0 || (dateRange.length > 0 && isValidDateRange)) {
      const params = { dateRange };

      if (pieTasksType === 'assignedToMe') {
        Object.assign(params, {
          taskAssignee: user.personId,
        });
      }

      dispatch(getTaskDataChartRequest(params));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isValidDateRange, dateRange, pieTasksType]);

  function getProcessCountByStatus(params: IPaginateValues) {
    dispatch(getProcessesCountByStatusRequest(params));
  }

  useEffect(() => {
    const params = { page, limit: LIMIT, dateRange };
    getProcessCountByStatus(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function handleDateFilter() {
    if (isValidDateRange) {
      setDateRange(dateRange);
      const params = { page, limit: LIMIT, dateRange };
      dispatch(getProcessesCountByStatusRequest(params));
    }
  }

  useEffect(() => {
    handleDateFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidDateRange, dateRange]);

  return (
    <Container>
      <Box>
        <BarchartCard>
          <Header>
            <Content>
              <TitleWrapper>
                <h2>Execução de processos</h2>
                <Tooltip
                  title="Esse gráfico mostra a quantidade de execuções de processos e seus status de acordo com o período selecionado."
                  arrow
                >
                  <div>
                    <IoMdInformationCircle size={16} />
                  </div>
                </Tooltip>
              </TitleWrapper>
              <ul>
                <Square background="#EB0045" />
                <li>Concluído</li>
                <Square background="#671966" />
                <li>Em andamento</li>
                <Square background="#FF99B8" />
                <li>Cancelado</li>
              </ul>
            </Content>
            <Calendar
              useContainer
              isValidRange={(isValid) => setIsValidDateRange(isValid)}
              getRange={(range) => setDateRange(range)}
            />
          </Header>

          <Wrapper>
            {page <= 1 ? (
              <LeftButton
                style={{ opacity: 0, pointerEvents: 'none' }}
                onClick={() => setPage(page - 1)}
                type="button"
              >
                <img src={ArrowImg} alt="" />
              </LeftButton>
            ) : (
              <LeftButton onClick={() => setPage(page - 1)} type="button">
                <img src={ArrowImg} alt="" />
              </LeftButton>
            )}

            <BarCharts data={processesCountByStatus} />
            {page < Math.ceil(processesTotalCount / LIMIT) ? (
              <RightButton onClick={() => setPage(page + 1)} type="button">
                <img src={ArrowImg} alt="" />
              </RightButton>
            ) : (
              <RightButton
                disabled
                style={{ opacity: 0, pointerEvents: 'none' }}
                onClick={() => setPage(page + 1)}
                type="button"
              >
                <img src={ArrowImg} alt="" />
              </RightButton>
            )}
          </Wrapper>
        </BarchartCard>
        <Grid container spacing={8} style={{ marginTop: '36px' }}>
          <Grid item xs={6}>
            <Card>
              <TitleWrapper>
                <Typography gutterBottom variant="h5" component="h2">
                  Tarefas finalizadas
                </Typography>
                <Tooltip
                  title="Esse gráfico mostra a quantidade de tarefas finalizadas sendo concluídas ou canceladas, de acordo com o período selecionado."
                  arrow
                >
                  <div>
                    <IoMdInformationCircle size={16} />
                  </div>
                </Tooltip>
              </TitleWrapper>

              <FormControl>
                <RadioGroup row>
                  <FormControlLabel
                    value="allTasks"
                    control={<Radio />}
                    label="Todas"
                    checked={pieTasksType === 'allTasks'}
                    onChange={(event) => handlePieTaskType(event)}
                  />
                  <FormControlLabel
                    value="assignedToMe"
                    control={<Radio />}
                    label="Minhas"
                    checked={pieTasksType === 'assignedToMe'}
                    onChange={(event) => handlePieTaskType(event)}
                  />
                </RadioGroup>
              </FormControl>
              <CardContent>
                <Pie data={tasksDataChart} loading={tasksDataChartLoading} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <TitleWrapper>
                <Typography gutterBottom variant="h5" component="h2">
                  Tarefas pendentes
                </Typography>
                <Tooltip
                  title="Esse gráfico mostra a quantidade de tarefas pendentes com seus status de atribuição e prazos."
                  arrow
                >
                  <div>
                    <IoMdInformationCircle size={16} />
                  </div>
                </Tooltip>
              </TitleWrapper>

              <FormControl>
                <RadioGroup row>
                  <FormControlLabel
                    value="assignedToMe"
                    control={<Radio />}
                    label="Minhas"
                    checked={tasksType === 'assignedToMe'}
                    onChange={(event) => handleChangeRadio(event)}
                  />
                  <FormControlLabel
                    value="assignedToOthers"
                    control={<Radio />}
                    label="De outros"
                    checked={tasksType === 'assignedToOthers'}
                    onChange={(event) => handleChangeRadio(event)}
                  />
                  <FormControlLabel
                    value="unassignedTasks"
                    control={<Radio />}
                    label="Disponíveis"
                    checked={tasksType === 'unassignedTasks'}
                    onChange={(event) => handleChangeRadio(event)}
                  />
                </RadioGroup>
              </FormControl>
              <CardContent>
                <StatusBar data={tasksStatusCount} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* <TaskRanking dateRange={dateRange} isValid={isValidDateRange} /> */}
      </Box>
    </Container>
  );
};

export default Dashboard;
